import React, { useState, useEffect, useRef } from 'react';
import {
  Heart,
  MessageCircle,
  Sparkles,
  Flag,
  Send,
  Shield,
  RefreshCw,
  Globe,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  AtSign,
  X,
  Check,
  AlertTriangle,
  Loader2,
  Award,
} from 'lucide-react';
import { ReportModal } from './ReportModal.tsx';
import { FriendButton } from './FriendButton.tsx';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { formatPostTimestamp } from '../utils/dateFormat.ts';
import { TrophyBadge } from './TrophyBadge.tsx';
import { RegistrationRequiredCard } from './RegistrationRequiredCard.tsx';

interface CommunityFeedViewProps {
  isRegistered?: boolean;
  currentUser: {
    uid: string;
    username: string;
    displayName?: string;
    avatarUrl?: string | null;
    country?: string;
    role?: string;
  };
  onNavigateToGuidelines?: () => void;
  onOpenGuidelines?: () => void;
  onNavigateToProfile?: (subTab?: string) => void;
  onOpenReport?: (target: {
    type: 'profile_picture' | 'feed_post' | 'feed_comment';
    id: string;
    targetUserUid: string;
    label?: string;
  }) => void;
  focusedPostId?: number | null;
  onClearFocusedPost?: () => void;
  onOpenUserProfile?: (uidOrUsername: string) => void;
}

export type FeedReactionType = 'like' | 'heart' | 'haha' | 'sad' | 'pray';

export interface FeedPost {
  id: number;
  userUid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  country: string;
  postType?: 'milestone' | 'challenge_mode' | 'text_only' | string;
  achievementCode: string;
  achievementTitle: string;
  score?: number;
  accuracy?: number;
  bestStreak?: number;
  xpGained?: number;
  userRank?: string;
  churchName?: string;
  content: string;
  reactions: Record<FeedReactionType, number>;
  userReaction?: FeedReactionType | null;
  commentCount: number;
  createdAt: string;
  updatedAt?: string;
  comments?: FeedComment[];
}

export interface FeedComment {
  id: number;
  postId: number;
  userUid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
}

interface FriendOption {
  uid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
}

const REACTION_CONFIG: Array<{
  type: FeedReactionType;
  label: string;
  emoji: string;
  activeColor: string;
}> = [
  { type: 'like', label: 'Like', emoji: '👍', activeColor: 'text-[#F97316] bg-[#F97316]/15 border-[#F97316]/40' },
  { type: 'heart', label: 'Heart', emoji: '❤️', activeColor: 'text-rose-600 bg-rose-950/40 border-rose-700' },
  { type: 'haha', label: 'Haha', emoji: '😂', activeColor: 'text-amber-600 bg-amber-950/40 border-amber-700' },
  { type: 'sad', label: 'Sad', emoji: '😢', activeColor: 'text-[#FAFAFA] bg-[#1C1C1C] border-[#404040]' },
  { type: 'pray', label: 'Pray', emoji: '🙏', activeColor: 'text-[#FAFAFA] bg-[#1C1C1C] border-[#404040]' },
];

export const CommunityFeedView: React.FC<CommunityFeedViewProps> = ({
  isRegistered = false,
  currentUser,
  onNavigateToGuidelines,
  onOpenGuidelines,
  onNavigateToProfile,
  onOpenReport,
  focusedPostId,
  onClearFocusedPost,
  onOpenUserProfile,
}) => {
  if (!isRegistered) {
    return (
      <RegistrationRequiredCard
        title="Join the Quick!Bible Community"
        description="Connect with Bible scholars worldwide. Share your Scripture milestones, celebrate achievements, challenge friends, and grow together in God's Word."
        onSignUp={() => onNavigateToProfile?.('auth_signup')}
        onSignIn={() => onNavigateToProfile?.('auth_signin')}
      />
    );
  }

  const triggerGuidelines = onOpenGuidelines || onNavigateToGuidelines;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('qb_auth_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  // Top Post Composer state
  const [composerText, setComposerText] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);

  // Edit Post state
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // Deletion Modal State
  const [deletingTarget, setDeletingTarget] = useState<{
    type: 'post' | 'comment';
    id: number;
    postId?: number;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Comments State
  const [openCommentsForPost, setOpenCommentsForPost] = useState<Record<number, boolean>>({});
  const [commentsByPost, setCommentsByPost] = useState<Record<number, FeedComment[]>>({});
  const [commentInputs, setCommentInputs] = useState<Record<number, string>>({});
  const [commentErrors, setCommentErrors] = useState<Record<number, string>>({});
  const [isSubmittingComment, setIsSubmittingComment] = useState<Record<number, boolean>>({});
  const [highlightedPostId, setHighlightedPostId] = useState<number | null>(null);

  // Friend Tagging & Mention Suggestions State
  const [friendsList, setFriendsList] = useState<FriendOption[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState<string | null>(null);
  const [activeTagTarget, setActiveTagTarget] = useState<{
    field: 'composer' | 'edit' | `comment-${number}`;
    position: number;
  } | null>(null);

  // Reporting Modal state
  const [reportTarget, setReportTarget] = useState<{
    targetType: 'profile_picture' | 'feed_post' | 'feed_comment';
    targetId: string;
    targetUserUid: string;
    targetUsername?: string;
  } | null>(null);

  const composerTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Load user's friends list for @Username autocomplete
  useEffect(() => {
    if (!currentUser?.uid) return;
    fetch(`/api/friends?uid=${encodeURIComponent(currentUser.uid)}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.friends) {
          setFriendsList(
            data.friends.map((f: any) => ({
              uid: f.uid,
              username: f.username,
              displayName: f.displayName || f.username,
              avatarUrl: f.avatarUrl || '',
            }))
          );
        }
      })
      .catch(() => {});
  }, [currentUser?.uid]);

  const triggerReport = (t: {
    targetType: 'profile_picture' | 'feed_post' | 'feed_comment';
    targetId: string;
    targetUserUid: string;
    targetUsername?: string;
  }) => {
    if (onOpenReport) {
      onOpenReport({
        type: t.targetType,
        id: t.targetId,
        targetUserUid: t.targetUserUid,
        label: t.targetUsername ? `@${t.targetUsername}` : undefined,
      });
    } else {
      setReportTarget(t);
    }
  };

  const loadFeed = async (page = 1, append = false) => {
    if (page === 1) setIsLoading(true);
    else setIsLoadingMore(true);

    try {
      const res = await fetch(
        `/api/feed/posts?uid=${encodeURIComponent(currentUser.uid)}&page=${page}&limit=20`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const newPosts: FeedPost[] = data.posts || [];
        setHasMorePosts(newPosts.length === 20);

        if (append) {
          setPosts((prev) => {
            const existingIds = new Set(prev.map((p) => p.id));
            const filtered = newPosts.filter((p) => !existingIds.has(p.id));
            return [...prev, ...filtered];
          });
        } else {
          setPosts(newPosts);
        }
        setCurrentPage(page);
      }
    } catch {
      // Handled gracefully
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFeed(1, false);
  }, [currentUser.uid]);

  useEffect(() => {
    if (!focusedPostId) return;
    const targetId = Number(focusedPostId);
    setHighlightedPostId(targetId);

    const postFound = posts.find((p) => p.id === targetId);
    if (!postFound) {
      loadFeed(1, false);
    } else if (!openCommentsForPost[targetId]) {
      setOpenCommentsForPost((prev) => ({ ...prev, [targetId]: true }));
      fetch(`/api/feed/posts/${targetId}/comments`, {
        headers: getAuthHeaders(),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.comments) {
            setCommentsByPost((prev) => ({ ...prev, [targetId]: data.comments }));
          }
        })
        .catch(() => {});
    }

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="feed-post-${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onClearFocusedPost?.();
    }, 150);

    const clearHighlightTimer = setTimeout(() => {
      setHighlightedPostId((prev) => (prev === targetId ? null : prev));
    }, 4000);

    return () => {
      clearTimeout(timer);
      clearTimeout(clearHighlightTimer);
    };
  }, [focusedPostId, posts.length]);

  // Handle @mention autocomplete typing detection
  const handleInputChangeWithMention = (
    text: string,
    field: 'composer' | 'edit' | `comment-${number}`,
    cursorPos: number
  ) => {
    const textBeforeCursor = text.slice(0, cursorPos);
    const lastAtIdx = textBeforeCursor.lastIndexOf('@');

    if (lastAtIdx !== -1) {
      const charBeforeAt = lastAtIdx > 0 ? textBeforeCursor[lastAtIdx - 1] : ' ';
      if (/\s|^/.test(charBeforeAt)) {
        const query = textBeforeCursor.slice(lastAtIdx + 1);
        if (!/\s/.test(query)) {
          setTagSearchQuery(query.toLowerCase());
          setActiveTagTarget({ field, position: lastAtIdx });
          return;
        }
      }
    }
    setTagSearchQuery(null);
    setActiveTagTarget(null);
  };

  const selectFriendTag = (friend: FriendOption) => {
    if (!activeTagTarget) return;
    const { field, position } = activeTagTarget;

    const insertTag = (currentText: string) => {
      const before = currentText.slice(0, position);
      const after = currentText.slice(position).replace(/^@[a-zA-Z0-9_]*/, '');
      return `${before}@${friend.username} ${after}`;
    };

    if (field === 'composer') {
      const next = insertTag(composerText);
      setComposerText(next.slice(0, 100));
    } else if (field === 'edit') {
      const next = insertTag(editContent);
      setEditContent(next.slice(0, 100));
    } else if (field.startsWith('comment-')) {
      const postId = Number(field.replace('comment-', ''));
      const curr = commentInputs[postId] || '';
      const next = insertTag(curr);
      setCommentInputs((prev) => ({ ...prev, [postId]: next.slice(0, 200) }));
    }

    setTagSearchQuery(null);
    setActiveTagTarget(null);
  };

  // Publish Text-Only Post
  const handlePublishPost = async (e: React.FormEvent) => {
    e.preventDefault();
    setComposerError(null);
    const clean = composerText.trim();
    if (!clean) return;

    if (clean.length > 100) {
      setComposerError('Post exceeds maximum length of 100 characters.');
      return;
    }

    if (/(https?:\/\/|www\.|\.com|\.org|\.net|\.io|\.gg)/i.test(clean)) {
      setComposerError('External links and URLs are not permitted in community feed posts.');
      return;
    }

    setIsPublishing(true);
    try {
      const res = await fetch('/api/feed/posts', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUser.uid,
          username: currentUser.username,
          userAvatar: currentUser.avatarUrl,
          content: clean,
          postType: 'text_only',
        }),
      });

      if (res.ok) {
        soundEngine.playCorrect();
        const data = await res.json();
        if (data.post) {
          setPosts((prev) => [data.post, ...prev]);
        }
        setComposerText('');
        setTagSearchQuery(null);
        setActiveTagTarget(null);
      } else {
        const errData = await res.json();
        setComposerError(errData.error || 'Failed to publish post.');
      }
    } catch {
      setComposerError('Unable to publish post. Please check connection.');
    } finally {
      setIsPublishing(false);
    }
  };

  // Edit Post
  const handleSaveEditPost = async (postId: number) => {
    const clean = editContent.trim();
    if (!clean) return;

    if (clean.length > 100) {
      alert('Post exceeds 100 characters.');
      return;
    }

    setIsSubmittingEdit(true);
    try {
      const res = await fetch(`/api/feed/posts/${postId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUser.uid,
          content: clean,
        }),
      });

      if (res.ok) {
        soundEngine.playClick();
        const data = await res.json();
        if (data.post) {
          setPosts((prev) =>
            prev.map((p) => (p.id === postId ? { ...p, content: data.post.content, updatedAt: data.post.updatedAt } : p))
          );
        }
        setEditingPostId(null);
        setEditContent('');
      } else {
        const errData = await res.json();
        alert(errData.error || 'Failed to edit post.');
      }
    } catch {
      alert('Failed to edit post.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Delete Post or Comment
  const handleConfirmDelete = async () => {
    if (!deletingTarget) return;
    setIsDeleting(true);
    try {
      if (deletingTarget.type === 'post') {
        const res = await fetch(`/api/feed/posts/${deletingTarget.id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ userUid: currentUser.uid }),
        });
        if (res.ok) {
          soundEngine.playClick();
          setPosts((prev) => prev.filter((p) => p.id !== deletingTarget.id));
        }
      } else if (deletingTarget.type === 'comment' && deletingTarget.postId) {
        const pId = deletingTarget.postId;
        const res = await fetch(`/api/feed/comments/${deletingTarget.id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ userUid: currentUser.uid }),
        });
        if (res.ok) {
          soundEngine.playClick();
          setCommentsByPost((prev) => ({
            ...prev,
            [pId]: (prev[pId] || []).filter((c) => c.id !== deletingTarget.id),
          }));
          setPosts((prev) =>
            prev.map((p) => (p.id === pId ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p))
          );
        }
      }
    } catch {
      // Ignore
    } finally {
      setIsDeleting(false);
      setDeletingTarget(null);
    }
  };

  // Reactions
  const handleToggleReaction = async (postId: number, reactionType: FeedReactionType) => {
    soundEngine.playClick();
    setPosts((prevPosts) =>
      prevPosts.map((p) => {
        if (p.id !== postId) return p;
        const currentRx = p.userReaction;
        const nextRx = currentRx === reactionType ? null : reactionType;
        const newCounts = { ...p.reactions };

        if (currentRx) {
          newCounts[currentRx] = Math.max(0, (newCounts[currentRx] || 0) - 1);
        }
        if (nextRx) {
          newCounts[nextRx] = (newCounts[nextRx] || 0) + 1;
        }

        return {
          ...p,
          userReaction: nextRx,
          reactions: newCounts,
        };
      })
    );

    try {
      const res = await fetch(`/api/feed/posts/${postId}/react`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUser.uid,
          reactionType,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? { ...p, reactions: data.reactions, userReaction: data.userReaction }
              : p
          )
        );
      }
    } catch {
      // Handled
    }
  };

  // Comments
  const handleToggleComments = async (postId: number) => {
    soundEngine.playClick();
    const currentlyOpen = openCommentsForPost[postId];
    setOpenCommentsForPost((prev) => ({ ...prev, [postId]: !currentlyOpen }));

    if (!currentlyOpen && !commentsByPost[postId]) {
      try {
        const res = await fetch(`/api/feed/posts/${postId}/comments`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setCommentsByPost((prev) => ({ ...prev, [postId]: data.comments || [] }));
        }
      } catch {
        // Handled
      }
    }
  };

  const handleCommentSubmit = async (postId: number, e: React.FormEvent) => {
    e.preventDefault();
    const text = (commentInputs[postId] || '').trim();
    if (!text) return;

    if (/(https?:\/\/|www\.|\.com|\.org|\.net|\.io|\.gg)/i.test(text)) {
      setCommentErrors((prev) => ({
        ...prev,
        [postId]: 'Links are not allowed in community comments per Community Guidelines.',
      }));
      return;
    }
    if (text.length > 200) {
      setCommentErrors((prev) => ({
        ...prev,
        [postId]: 'Comments cannot exceed 200 characters.',
      }));
      return;
    }

    setCommentErrors((prev) => ({ ...prev, [postId]: '' }));
    setIsSubmittingComment((prev) => ({ ...prev, [postId]: true }));

    try {
      const res = await fetch(`/api/feed/posts/${postId}/comments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUser.uid,
          username: currentUser.username,
          userAvatar: currentUser.avatarUrl,
          content: text,
        }),
      });

      if (res.ok) {
        soundEngine.playCorrect();
        const data = await res.json();
        const rawComment = data.comment || {};
        const newComment: FeedComment = {
          id: rawComment.id || Date.now(),
          postId,
          userUid: rawComment.userUid || currentUser.uid,
          username: rawComment.username || currentUser.username,
          displayName:
            rawComment.displayName || currentUser.displayName || currentUser.username,
          avatarUrl:
            rawComment.avatarUrl !== undefined
              ? rawComment.avatarUrl
              : currentUser.avatarUrl || '',
          content: rawComment.content || text,
          createdAt: rawComment.createdAt || new Date().toISOString(),
        };
        setCommentsByPost((prev) => ({
          ...prev,
          [postId]: [...(prev[postId] || []), newComment],
        }));
        setCommentInputs((prev) => ({ ...prev, [postId]: '' }));
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p
          )
        );
      }
    } catch {
      // Ignore
    } finally {
      setIsSubmittingComment((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // Render text content with clickable @username mentions styled in QuickiBible orange
  const renderContentWithMentions = (content: string) => {
    const parts = content.split(/(@[a-zA-Z0-9_]+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@') && part.length > 1) {
        const mentionName = part.slice(1);
        return (
          <button
            key={index}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenUserProfile) onOpenUserProfile(mentionName);
            }}
            className="text-[#F97316] font-semibold hover:underline inline-baseline px-0.5 cursor-pointer rounded-xs focus:outline-none focus:ring-1 focus:ring-[#F97316]"
            title={`View @${mentionName}'s profile`}
          >
            @{mentionName}
          </button>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Filtered friends for autocomplete
  const filteredFriendsForTag = friendsList.filter((f) => {
    if (!tagSearchQuery) return true;
    return (
      f.username.toLowerCase().includes(tagSearchQuery) ||
      f.displayName.toLowerCase().includes(tagSearchQuery)
    );
  });

  return (
    <div className="w-full max-w-4xl mx-auto py-6 sm:py-8 px-4 sm:px-6 space-y-6 box-border flex-1 flex flex-col">
      {/* Top Banner with Guidelines Navigation */}
      <div className="rounded-3xl bg-[#161618] border border-[#27272A] text-white p-5 sm:p-7 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#F97316]/15 text-[#F97316] text-[11px] font-bold uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5" />
              Global Fellowship
            </span>
            <span className="text-xs text-[#A3A3A3]">Community Feed</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">
            Scripture Scholars Worldwide
          </h1>
          <p className="text-xs sm:text-sm text-[#D4D4D4] max-w-xl">
            Celebrate Scripture achievements, quiz milestones, reflections, and encouragement with believers around the globe.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={triggerGuidelines}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#404040] text-xs font-semibold text-white transition-colors cursor-pointer"
          >
            <Shield className="w-3.5 h-3.5 text-[#F97316]" />
            <span>Community Guidelines</span>
          </button>
          <button
            type="button"
            onClick={() => loadFeed(1, false)}
            title="Refresh Feed"
            aria-label="Refresh Community Feed"
            className="p-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#404040] text-white transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* FACEBOOK-STYLE TEXT COMPOSER ("What's on your mind, Bible Quiz Warrior?") */}
      <div className="relative rounded-3xl bg-[#141414] border border-[#262626] p-4 sm:p-5 shadow-md space-y-3">
        <div className="flex items-start gap-3">
          {currentUser.avatarUrl ? (
            <img
              src={currentUser.avatarUrl}
              alt={currentUser.displayName || currentUser.username}
              className="w-10 h-10 rounded-full object-cover border border-[#262626] shrink-0 mt-0.5"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
              {currentUser.username.slice(0, 2).toUpperCase()}
            </div>
          )}

          <div className="flex-1 space-y-2 relative">
            <textarea
              ref={composerTextareaRef}
              rows={2}
              maxLength={100}
              placeholder="What's on your mind, Bible Quiz Warrior?"
              value={composerText}
              onChange={(e) => {
                const val = e.target.value.slice(0, 100);
                setComposerText(val);
                handleInputChangeWithMention(val, 'composer', e.target.selectionStart || val.length);
              }}
              onKeyUp={(e) => {
                const target = e.target as HTMLTextAreaElement;
                handleInputChangeWithMention(target.value, 'composer', target.selectionStart || target.value.length);
              }}
              className="w-full bg-[#1C1C1C] border border-[#262626] focus:border-[#F97316] rounded-2xl p-3 text-xs sm:text-sm text-[#FAFAFA] placeholder-[#737373] focus:outline-none focus:ring-1 focus:ring-[#F97316] resize-none transition-colors"
            />

            {/* Friend Tagging Autocomplete Suggestion Dropdown */}
            {activeTagTarget && activeTagTarget.field === 'composer' && filteredFriendsForTag.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-2xl bg-[#1C1C1C] border border-[#404040] shadow-2xl overflow-hidden max-h-48 overflow-y-auto divide-y divide-[#262626]">
                <div className="px-3 py-1.5 bg-[#141414] text-[10px] font-bold text-[#F97316] uppercase tracking-wider flex items-center gap-1">
                  <AtSign className="w-3 h-3" />
                  <span>Tag a Friend</span>
                </div>
                {filteredFriendsForTag.map((friend) => (
                  <button
                    key={friend.uid}
                    type="button"
                    onClick={() => selectFriendTag(friend)}
                    className="w-full text-left px-3 py-2 hover:bg-[#262626] flex items-center gap-2.5 transition-colors cursor-pointer"
                  >
                    {friend.avatarUrl ? (
                      <img src={friend.avatarUrl} alt={friend.displayName} className="w-6 h-6 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#141414] text-[#A3A3A3] font-bold text-[10px] flex items-center justify-center shrink-0">
                        {friend.username.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1 truncate">
                      <span className="text-xs font-semibold text-white block truncate">{friend.displayName}</span>
                      <span className="text-[10px] text-[#A3A3A3] font-mono block truncate">@{friend.username}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {composerError && (
              <p className="text-xs text-rose-500 font-medium">{composerError}</p>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className={`text-[11px] font-mono font-medium ${composerText.length >= 100 ? 'text-rose-400 font-bold' : 'text-[#737373]'}`}>
                {composerText.length}/100
              </span>

              <button
                type="button"
                onClick={handlePublishPost}
                disabled={isPublishing || !composerText.trim() || composerText.length > 100}
                className="px-4 py-1.5 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 disabled:opacity-50 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    <span>Publish Post</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feed Posts List */}
      {isLoading && posts.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <div className="w-10 h-10 border-4 border-[#525252] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-[#A3A3A3] font-medium">Loading community feed...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-3xl bg-[#141414] border border-[#262626] p-8 text-center space-y-3 shadow-xs">
          <Sparkles className="w-10 h-10 text-amber-500 mx-auto" />
          <h2 className="text-base font-bold text-[#FAFAFA]">
            No community posts yet
          </h2>
          <p className="text-xs text-[#A3A3A3] max-w-md mx-auto">
            Be the first to share a reflection or complete a quiz to inspire Bible Quiz Warriors!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const isOwner = post.userUid === currentUser.uid || currentUser.role === 'admin';
            const isEditing = editingPostId === post.id;
            const isCommentsOpen = Boolean(openCommentsForPost[post.id]);
            const comments = commentsByPost[post.id] || [];
            const isTextOnly = post.postType === 'text_only' || post.achievementCode === 'TEXT_POST';
            const formattedTime = formatPostTimestamp(post.createdAt);

            return (
              <article
                key={post.id}
                data-testid={`feed-post-${post.id}`}
                className={`rounded-3xl bg-[#141414] border p-4 sm:p-6 shadow-xs space-y-3.5 transition-all ${
                  highlightedPostId === post.id
                    ? 'border-[#F97316] ring-1 ring-[#F97316]/40'
                    : 'border-[#262626]'
                }`}
              >
                {/* Author Profile Header Row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {/* User profile picture or avatar initials */}
                    <div className="relative group/avatar shrink-0 mt-0.5">
                      <div
                        onClick={() => onOpenUserProfile && onOpenUserProfile(post.userUid)}
                        className="cursor-pointer hover:opacity-85 transition-opacity"
                        title={`View @${post.username}'s profile`}
                      >
                        {post.avatarUrl ? (
                          <img
                            src={post.avatarUrl}
                            alt={post.displayName}
                            className="w-11 h-11 rounded-2xl object-cover border border-[#262626] shadow-xs"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] font-bold text-sm flex items-center justify-center shadow-xs">
                            {post.username.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                      {post.avatarUrl && (
                        <button
                          type="button"
                          onClick={() =>
                            triggerReport({
                              targetType: 'profile_picture',
                              targetId: String(post.id),
                              targetUserUid: post.userUid,
                              targetUsername: post.username,
                            })
                          }
                          title="Report Profile Picture"
                          aria-label={`Report profile picture of @${post.username}`}
                          className="absolute -top-1 -right-1 hidden group-hover/avatar:flex w-5 h-5 rounded-full bg-red-600 text-white items-center justify-center shadow-sm"
                        >
                          <Flag className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => onOpenUserProfile && onOpenUserProfile(post.userUid)}
                          className="font-bold text-sm text-[#FAFAFA] hover:text-amber-400 transition-colors cursor-pointer truncate"
                        >
                          {post.displayName}
                        </button>

                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-[#1C1C1C] text-[#FAFAFA] font-semibold border border-[#262626]">
                          {post.userRank || 'Scholar'}
                        </span>

                        {currentUser.uid !== post.userUid && (
                          <FriendButton
                            currentUserUid={currentUser.uid}
                            targetUserUid={post.userUid}
                            size="xs"
                          />
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-[11px] text-[#A3A3A3] mt-0.5 flex-wrap">
                        <span className="font-mono text-[#D4D4D4]">@{post.username}</span>
                        <span>•</span>
                        <span>{formattedTime}</span>
                        {post.churchName && (
                          <>
                            <span>•</span>
                            <span className="text-[#A3A3A3] truncate max-w-[180px]">
                              {post.churchName}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Top Right Controls: Owner Edit / Delete OR Report */}
                  <div className="flex items-center gap-1 shrink-0">
                    {isOwner ? (
                      <div className="flex items-center gap-1">
                        {isTextOnly && (
                          <button
                            type="button"
                            onClick={() => {
                              soundEngine.playClick();
                              setEditingPostId(editingPostId === post.id ? null : post.id);
                              setEditContent(post.content);
                            }}
                            className="p-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#A3A3A3] hover:text-white transition-colors cursor-pointer"
                            title="Edit Post"
                            aria-label="Edit post"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            soundEngine.playClick();
                            setDeletingTarget({ type: 'post', id: post.id });
                          }}
                          className="p-1.5 rounded-lg bg-[#1C1C1C] hover:bg-rose-950/60 border border-[#262626] text-[#A3A3A3] hover:text-rose-400 transition-colors cursor-pointer"
                          title="Delete Post"
                          aria-label="Delete post"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          triggerReport({
                            targetType: 'feed_post',
                            targetId: String(post.id),
                            targetUserUid: post.userUid,
                            targetUsername: post.username,
                          })
                        }
                        title="Report Feed Post"
                        aria-label={`Report feed post by @${post.username}`}
                        className="text-[#A3A3A3] hover:text-red-500 p-1.5 rounded-lg hover:bg-[#1C1C1C] transition-colors cursor-pointer"
                      >
                        <Flag className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Edit Post Area */}
                {isEditing ? (
                  <div className="space-y-2 p-3 rounded-2xl bg-[#1C1C1C] border border-[#F97316]/50">
                    <textarea
                      ref={editTextareaRef}
                      rows={2}
                      maxLength={100}
                      value={editContent}
                      onChange={(e) => {
                        const val = e.target.value.slice(0, 100);
                        setEditContent(val);
                        handleInputChangeWithMention(val, 'edit', e.target.selectionStart || val.length);
                      }}
                      className="w-full bg-[#141414] border border-[#262626] focus:border-[#F97316] rounded-xl p-2.5 text-xs sm:text-sm text-[#FAFAFA] focus:outline-none resize-none"
                    />

                    {/* Friend Tagging Autocomplete in Edit Mode */}
                    {activeTagTarget && activeTagTarget.field === 'edit' && filteredFriendsForTag.length > 0 && (
                      <div className="z-50 rounded-xl bg-[#141414] border border-[#404040] shadow-xl overflow-hidden max-h-36 overflow-y-auto divide-y divide-[#262626]">
                        {filteredFriendsForTag.map((friend) => (
                          <button
                            key={friend.uid}
                            type="button"
                            onClick={() => selectFriendTag(friend)}
                            className="w-full text-left px-3 py-1.5 hover:bg-[#262626] flex items-center gap-2 transition-colors cursor-pointer"
                          >
                            <span className="text-xs font-semibold text-white truncate">{friend.displayName}</span>
                            <span className="text-[10px] text-[#A3A3A3] font-mono truncate">@{friend.username}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[10px] font-mono text-[#737373]">{editContent.length}/100</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingPostId(null)}
                          className="px-3 py-1 rounded-lg bg-[#262626] text-xs font-semibold text-[#A3A3A3] hover:text-white transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEditPost(post.id)}
                          disabled={isSubmittingEdit || !editContent.trim()}
                          className="px-3 py-1 rounded-lg bg-[#F97316] hover:bg-[#FB923C] text-black text-xs font-bold transition-colors cursor-pointer disabled:opacity-50"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Post Body Content with clickable @mentions */
                  <div className="text-sm text-[#FAFAFA] leading-relaxed break-words whitespace-pre-line">
                    {renderContentWithMentions(post.content)}
                  </div>
                )}

                {/* Milestone Quiz Performance Card if Milestone or Challenge post */}
                {!isTextOnly && (
                  <div className="p-3.5 rounded-2xl bg-[#1C1C1C] border border-[#262626] flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0">
                        <Award className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-white">{post.achievementTitle}</div>
                        <div className="text-[11px] text-[#A3A3A3] font-mono mt-0.5">
                          Score: {post.score} · Accuracy: {post.accuracy}% · XP: +{post.xpGained}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Post Footer Row: Reactions Bar & Comment Toggle */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#262626] flex-wrap">
                  {/* Like / Reaction Buttons Bar */}
                  <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
                    {REACTION_CONFIG.map((rx) => {
                      const count = post.reactions?.[rx.type] || 0;
                      const isSelected = post.userReaction === rx.type;

                      return (
                        <button
                          key={rx.type}
                          type="button"
                          data-testid={`reaction-btn-${rx.type}-${post.id}`}
                          onClick={() => handleToggleReaction(post.id, rx.type)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                            isSelected
                              ? rx.activeColor
                              : 'bg-[#1C1C1C] hover:bg-[#262626] border-[#262626] text-[#A3A3A3] hover:text-white'
                          }`}
                        >
                          <span className="text-xs">{rx.emoji}</span>
                          {count > 0 && <span className="font-mono text-[11px]">{count}</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Comment Accordion Trigger */}
                  <button
                    type="button"
                    onClick={() => handleToggleComments(post.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#D4D4D4] hover:text-white transition-colors cursor-pointer"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-[#F97316]" />
                    <span>
                      {post.commentCount} {post.commentCount === 1 ? 'Comment' : 'Comments'}
                    </span>
                    {isCommentsOpen ? <ChevronUp className="w-3 h-3 text-[#A3A3A3]" /> : <ChevronDown className="w-3 h-3 text-[#A3A3A3]" />}
                  </button>
                </div>

                {/* Comments Accordion */}
                {isCommentsOpen && (
                  <div className="pt-3 border-t border-[#262626] space-y-3">
                    {comments.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                        {comments.map((comment) => {
                          const isCommentOwner = comment.userUid === currentUser.uid || currentUser.role === 'admin';
                          const commentInitials = (comment.username || comment.displayName || 'SC').slice(0, 2).toUpperCase();
                          const commentTime = formatPostTimestamp(comment.createdAt);

                          return (
                            <div
                              key={comment.id}
                              data-testid={`feed-comment-${comment.id}`}
                              className="p-2.5 rounded-xl bg-[#1C1C1C] border border-[#262626] text-xs flex items-start justify-between gap-2.5"
                            >
                              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                <div
                                  onClick={() => onOpenUserProfile && onOpenUserProfile(comment.userUid)}
                                  className="cursor-pointer shrink-0 hover:opacity-85 transition-opacity"
                                  title={`View @${comment.username}'s profile`}
                                >
                                  {comment.avatarUrl ? (
                                    <img
                                      src={comment.avatarUrl}
                                      alt={comment.displayName || comment.username}
                                      className="w-8 h-8 rounded-full object-cover border border-[#262626] shrink-0"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-[#141414] border border-[#262626] text-[#A3A3A3] font-bold text-xs flex items-center justify-center shrink-0">
                                      {commentInitials}
                                    </div>
                                  )}
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 font-semibold text-[#FAFAFA] flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => onOpenUserProfile && onOpenUserProfile(comment.userUid)}
                                      className="truncate font-semibold text-[#FAFAFA] hover:text-amber-400 transition-colors cursor-pointer text-left"
                                    >
                                      {comment.displayName}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => onOpenUserProfile && onOpenUserProfile(comment.userUid)}
                                      className="font-normal font-mono text-[10px] text-[#A3A3A3] hover:text-amber-400 transition-colors cursor-pointer"
                                    >
                                      @{comment.username}
                                    </button>
                                    <span className="text-[10px] text-[#737373] font-mono">• {commentTime}</span>
                                  </div>
                                  <p className="text-[#D4D4D4] mt-0.5 break-words">
                                    {renderContentWithMentions(comment.content)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-1 shrink-0">
                                {isCommentOwner ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      soundEngine.playClick();
                                      setDeletingTarget({ type: 'comment', id: comment.id, postId: post.id });
                                    }}
                                    title="Delete Comment"
                                    aria-label="Delete comment"
                                    className="text-[#A3A3A3] hover:text-rose-400 p-1 cursor-pointer transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      triggerReport({
                                        targetType: 'feed_comment',
                                        targetId: String(comment.id),
                                        targetUserUid: comment.userUid,
                                        targetUsername: comment.username,
                                      })
                                    }
                                    title="Report comment"
                                    aria-label={`Report comment by @${comment.username}`}
                                    className="text-[#A3A3A3] hover:text-red-500 p-1 cursor-pointer"
                                  >
                                    <Flag className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-[#A3A3A3] italic">
                        Be the first to leave an edifying comment!
                      </p>
                    )}

                    {commentErrors[post.id] && (
                      <p className="text-[11px] text-amber-400 font-medium">
                        {commentErrors[post.id]}
                      </p>
                    )}

                    {/* New comment form (max 200 chars, with friend tagging) */}
                    <div className="relative">
                      <form
                        onSubmit={(e) => handleCommentSubmit(post.id, e)}
                        className="flex gap-2"
                      >
                        <input
                          type="text"
                          maxLength={200}
                          placeholder="Write a comment (use @ to tag friends)..."
                          value={commentInputs[post.id] || ''}
                          onChange={(e) => {
                            const val = e.target.value.slice(0, 200);
                            setCommentInputs((prev) => ({ ...prev, [post.id]: val }));
                            handleInputChangeWithMention(val, `comment-${post.id}`, e.target.selectionStart || val.length);
                          }}
                          className="flex-1 rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2 text-xs text-[#FAFAFA] focus:outline-none focus:ring-1 focus:ring-[#F97316]"
                        />
                        <button
                          type="submit"
                          disabled={isSubmittingComment[post.id] || !(commentInputs[post.id] || '').trim()}
                          className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition-all shrink-0 cursor-pointer"
                        >
                          <Send className="w-3.5 h-3.5" />
                          <span>Reply</span>
                        </button>
                      </form>

                      {/* Autocomplete for Comments */}
                      {activeTagTarget && activeTagTarget.field === `comment-${post.id}` && filteredFriendsForTag.length > 0 && (
                        <div className="absolute left-0 right-0 bottom-full mb-1 z-50 rounded-xl bg-[#141414] border border-[#404040] shadow-xl overflow-hidden max-h-36 overflow-y-auto divide-y divide-[#262626]">
                          {filteredFriendsForTag.map((friend) => (
                            <button
                              key={friend.uid}
                              type="button"
                              onClick={() => selectFriendTag(friend)}
                              className="w-full text-left px-3 py-1.5 hover:bg-[#262626] flex items-center gap-2 transition-colors cursor-pointer"
                            >
                              <span className="text-xs font-semibold text-white truncate">{friend.displayName}</span>
                              <span className="text-[10px] text-[#A3A3A3] font-mono truncate">@{friend.username}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}

          {/* Load More Historical Posts Button */}
          {hasMorePosts && (
            <div className="pt-4 text-center">
              <button
                type="button"
                onClick={() => loadFeed(currentPage + 1, true)}
                disabled={isLoadingMore}
                className="px-6 py-2.5 rounded-2xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#404040] text-xs font-bold text-[#FAFAFA] hover:text-white transition-all inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-[#F97316]" />
                    <span>Loading older posts...</span>
                  </>
                ) : (
                  <span>Load More Community Posts</span>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal for Post & Comment Deletion */}
      {deletingTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="w-full max-w-sm rounded-3xl bg-[#141414] border border-[#262626] p-6 shadow-2xl space-y-4 text-center">
            <div className="w-12 h-12 rounded-full bg-rose-500/15 text-rose-500 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold text-white">
                Delete {deletingTarget.type === 'post' ? 'Community Post' : 'Comment'}?
              </h3>
              <p className="text-xs text-[#A3A3A3] leading-relaxed">
                Are you sure you want to permanently delete this {deletingTarget.type}? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingTarget(null)}
                disabled={isDeleting}
                className="flex-1 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] text-xs font-semibold text-[#A3A3A3] hover:text-white transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-xs font-bold text-white transition-colors cursor-pointer inline-flex items-center justify-center gap-1.5"
              >
                {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Report Modal */}
      {reportTarget && (
        <ReportModal
          isOpen={true}
          onClose={() => setReportTarget(null)}
          reporterUid={currentUser.uid}
          reporterUsername={currentUser.username}
          targetType={reportTarget.targetType}
          targetId={reportTarget.targetId}
          targetUserUid={reportTarget.targetUserUid}
          targetUsername={reportTarget.targetUsername}
        />
      )}
    </div>
  );
};
